import { Cookie, serializeCookie } from "../common/cookie";
import { mapResponseToJson, mapResponseToText } from "../common/responses";
import { isAtlassianIdAuth } from "../common/xsrf-token";

import {
    ActivateProductsRequest,
    ActivateProductsResponse,
    CloudStatusResponse,
    CreateCloudRequest,
    CreateCloudResponse,
    GetCloudStatusOptions,
    GetNameAvailabilityOptions,
    GetNameAvailabilityResponseBody,
    ReserveNameOptions,
    ReserveNameResponseBody,
    RenameSiteOptions,
    RenameSiteResponseBody,
    PollProgressOptions,
    PollProgressResponseBody,
    ResourcePackagesCheckOptions,
    ResourcePackagesCheckResponseBody
} from "./cofs-client.types";

export const STAGING_BASE_URL = "https://cofs.staging.public.atl-paas.net";

export type CofsClientOptions = {
    readonly baseUrl?: string;
};

export interface ICofsClient {
    activateProducts(
        request: ActivateProductsRequest
    ): Promise<ActivateProductsResponse>;
    createCloud(
        originalRequest: CreateCloudRequest
    ): Promise<CreateCloudResponse>;
    getNameAvailability(options: GetNameAvailabilityOptions): Promise<boolean>;
    reserveName(options: ReserveNameOptions): Promise<ReserveNameResponseBody>;
    renameSite({ cloudId, newSiteName }: RenameSiteOptions): Promise<string>;
    pollProgress({
        progressUri
    }: PollProgressOptions): Promise<PollProgressResponseBody>;
    getCloudStatus(
        request: GetCloudStatusOptions
    ): Promise<CloudStatusResponse>;
    getHealthCheck(): Promise<string | undefined>;
    resourcePackagesCheck(
        options: ResourcePackagesCheckOptions
    ): Promise<ResourcePackagesCheckResponseBody>;
}

/**
 * @see: https://cofs.prod.public.atl-paas.net/api.html
 */
export class CofsClient implements ICofsClient {
    private readonly baseUrl: string;

    public constructor({
        baseUrl = process.env["COFS_CLIENT_BASE_URL"] ?? STAGING_BASE_URL
    }: CofsClientOptions = {}) {
        this.baseUrl = baseUrl;
    }

    public async activateProducts(
        request: ActivateProductsRequest
    ): Promise<ActivateProductsResponse> {
        const headers = new Headers({
            "Accept": "application/json",
            "Content-Type": "application/json"
        });
        const { cloudId, body, auth, analyticsData, userContextToken } =
            request;

        let cookiesList: Cookie[] = [];
        if (analyticsData) {
            cookiesList = analyticsData;
        }

        if (isAtlassianIdAuth(auth)) {
            headers.append("ATL-XSRF-Token", auth.xsrfToken);
            cookiesList.push(auth.cloudSessionTokenCookie);
        } else {
            headers.append("X-Slauth-Egress", "true");
            headers.append("X-Slauth-Audience", "cofs");
            if (userContextToken) {
                headers.append("User-Context", userContextToken);
            }
        }

        if (cookiesList.length) {
            headers.append(
                "Cookie",
                cookiesList
                    .map((cookieItem) => serializeCookie(cookieItem))
                    .join("; ")
            );
        }

        return mapResponseToJson(
            await fetch(`${this.baseUrl}/cloud/${cloudId}/activateProducts`, {
                method: "POST",
                headers,
                body: JSON.stringify(body)
            })
        );
    }

    public async createCloud(
        originalRequest: CreateCloudRequest
    ): Promise<CreateCloudResponse> {
        const request = originalRequest;
        const headers = new Headers({
            "Accept": "application/json",
            "Content-Type": "application/json"
        });
        const {
            body,
            auth,
            userContextToken,
            source,
            clientIp,
            analyticsData
        } = request;

        if (source) {
            headers.append("X-ATL-Source", source);
        }

        let cookiesList: Cookie[] = [];
        if (analyticsData) {
            cookiesList = analyticsData;
        }

        if (isAtlassianIdAuth(auth)) {
            headers.append("ATL-XSRF-Token", auth.xsrfToken);
            cookiesList.push(auth.cloudSessionTokenCookie);
        } else {
            headers.append("X-Slauth-Egress", "true");
            headers.append("X-Slauth-Audience", "cofs");
            if (userContextToken) {
                headers.append("User-Context", userContextToken);
            }
        }

        if (cookiesList.length) {
            headers.append(
                "Cookie",
                cookiesList
                    .map((cookieItem) => serializeCookie(cookieItem))
                    .join("; ")
            );
        }

        if (clientIp) {
            headers.append("X-Forwarded-For", clientIp);
        }

        return mapResponseToJson(
            await fetch(`${this.baseUrl}/cloud`, {
                method: "POST",
                headers,
                body: JSON.stringify(body)
            })
        );
    }

    public async getNameAvailability({
        name
    }: GetNameAvailabilityOptions): Promise<boolean> {
        const { success } =
            await mapResponseToJson<GetNameAvailabilityResponseBody>(
                await fetch(`${this.baseUrl}/nameAvailability`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        name
                    })
                })
            );

        return success;
    }

    public async reserveName({
        cloudName
    }: ReserveNameOptions): Promise<ReserveNameResponseBody> {
        const headers = new Headers({
            "Accept": "application/json",
            "Content-Type": "application/json",
            "X-Slauth-Egress": "true",
            "X-Slauth-Audience": "cofs"
        });

        return mapResponseToJson<ReserveNameResponseBody>(
            await fetch(`${this.baseUrl}/cloudname/availability/reserve`, {
                method: "POST",
                headers,
                body: JSON.stringify({
                    cloudName
                })
            })
        );
    }

    public async resourcePackagesCheck({
        entitlements
    }: ResourcePackagesCheckOptions): Promise<ResourcePackagesCheckResponseBody> {
        const headers = new Headers({
            "Accept": "application/json",
            "Content-Type": "application/json",
            "X-Slauth-Egress": "true",
            "X-Slauth-Audience": "cofs"
        });
        const packageUris = entitlements.map((entitlement) => {
            return `ari:cloud:platform::lifecycle-resource-package/${entitlement}`;
        });

        return mapResponseToJson<ResourcePackagesCheckResponseBody>(
            await fetch(`${this.baseUrl}/resource-packages/info`, {
                method: "POST",
                headers,
                body: JSON.stringify({
                    "package-uris": packageUris
                })
            })
        );
    }

    public async renameSite({
        cloudId,
        newSiteName
    }: RenameSiteOptions): Promise<string> {
        const { progressUri } = await mapResponseToJson<RenameSiteResponseBody>(
            await fetch(`${this.baseUrl}/${cloudId}/rename`, {
                method: "PUT",
                headers: {
                    "Content-Type": "application/json"
                },
                body: JSON.stringify({ cloudName: newSiteName })
            })
        );
        return progressUri;
    }

    public async getCloudStatus({
        cloudId,
        requestId
    }: GetCloudStatusOptions): Promise<CloudStatusResponse> {
        return mapResponseToJson(
            await fetch(
                `${this.baseUrl}/cloud/${cloudId}/status/${requestId}`,
                {
                    headers: {
                        "Content-Type": "application/json",
                        "X-Rate-Limit-Request-Id": requestId
                    }
                }
            )
        );
    }

    public async pollProgress({
        progressUri
    }: PollProgressOptions): Promise<PollProgressResponseBody> {
        return mapResponseToJson<PollProgressResponseBody>(
            await fetch(progressUri, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json"
                }
            })
        );
    }

    public async getHealthCheck(): Promise<string | undefined> {
        return mapResponseToText(
            await fetch(`${this.baseUrl}/healthcheck`, {
                method: "GET",

                headers: {
                    "Content-Type": "application/json"
                }
            })
        );
    }
}
